import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import IconButton from "./Button";
import {
  CloseFilledIcon,
  EllipseInfo,
  EllipseDelete,
  EllipseSave,
} from "./Icons";

const FullScreenOverlay = ({
  zIndex = 1000, // default z-index value
  activeSection,
  title,
  subtitle,
  buttons,
  children,
  width,
  height,
  isPopup = false, // decide small popups or fullscreen modal
  popupType = "info", // if popup type given as other then the icon on top will not show
  iconClose = false,
  onClose, // Add an onClose prop to handle closing the modal
  alignment = null,
  isSlimModal = false, // to decrease the header padding
  footerButtons = null, // array of buttons specifically for the bottom footer
}) => {
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // 2) In the effect, scroll that container (not window!)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [activeSection, scrollContainerRef]);

  return (
    <FullScreenOverlayWrapper zIndex={zIndex} className="full-screen-overlay">
      {/* Sticky Header */}
      {!isPopup ? (
        <ModalContainer
          width={width}
          height={height}
          $alignment={alignment}
          $isSlimModal={isSlimModal}
        >
          <ModalHeader
            $isSlimModal={isSlimModal}
            className="full-screen-overlay-header"
          >
            <ModalTitle>
              {title} {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
            </ModalTitle>

            <ButtonGroup>
              {/* Render the close icon if iconClose is true */}
              {iconClose ? (
                <CloseButton onClick={onClose}>
                  <CloseFilledIcon /> {/* Show the close icon */}
                </CloseButton>
              ) : (
                // Render dynamic buttons if iconClose is not passed
                buttons.map((button, index) => (
                  <IconButton
                    key={index}
                    variant={button.variant || "secondary"} // Pass button variant (e.g., primary, secondary, danger)
                    onClick={button.onClick}
                    state={button.state}
                    width={"165px"}
                  >
                    {button.label} {/* Button label */}
                  </IconButton>
                ))
              )}
            </ButtonGroup>
          </ModalHeader>
          <ModalContent ref={scrollContainerRef}>{children}</ModalContent>

          {/* Footer conditionally for bottom button with close onto */}
          {footerButtons && footerButtons.length > 0 && (
            <FooterContainer $isSlimModal={isSlimModal}>
              {footerButtons.map((btn, i) => (
                <IconButton
                  key={i}
                  variant={btn.variant || "secondary"}
                  onClick={btn.onClick}
                  state={btn.state}
                  width="165px"
                >
                  {btn.label}
                </IconButton>
              ))}
            </FooterContainer>
          )}
        </ModalContainer>
      ) : (
        <>
          <PopupContainer>
            {popupType === "info" && <EllipseInfo />}
            {popupType === "delete" && <EllipseDelete />}
            {popupType === "save" && <EllipseSave />}

            {children}
            <ButtonGroup>
              {/* Render the close icon if iconClose is true */}
              {iconClose ? (
                <IconButton variant="ghost" onClick={onClose}>
                  <CloseFilledIcon /> {/* Show the close icon */}
                </IconButton>
              ) : (
                // Render dynamic buttons if iconClose is not passed
                buttons.map((button, index) => (
                  <IconButton
                    key={index}
                    variant={button.variant || "secondary"} // Pass button variant (e.g., primary, secondary, danger)
                    onClick={button.onClick}
                    state={button.state}
                    width={"165px"}
                  >
                    {button.label} {/* Button label */}
                  </IconButton>
                ))
              )}
            </ButtonGroup>
          </PopupContainer>
        </>
      )}
    </FullScreenOverlayWrapper>
  );
};

export default FullScreenOverlay;

// Full-screen overlay modal
const FullScreenOverlayWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6); // Dim background
  z-index: ${(props) =>
    props.zIndex || 1000}; // Use the passed value or default to 1000
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto; // Allow scrolling if content overflows

  /* if viewport is wide but short (e.g. landscape phone), drop form below the header */
  @media (orientation: landscape) and (max-height: 500px) {
    align-items: flex-start;
    padding: 1rem 0;
  }
`;

// Modal container with fixed header
const ModalContainer = styled.div`
  background-color: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  /* if slim, inset 1rem gutters; else use passed width */
  width: ${({ width, $isSlimModal }) =>
    $isSlimModal ? `calc(100% - 2rem)` : width || "80%"};
  max-width: ${({ width, $isSlimModal }) => ($isSlimModal ? width : "none")};
  margin: ${({ $isSlimModal }) => ($isSlimModal ? "1rem auto" : "0")};

  height: ${({ height, $isSlimModal }) =>
    $isSlimModal ? "auto" : height || "90%"};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: ${({ height }) => (height === "100%" ? "none" : "12px")};

  /* Conditional styling based on alignment prop */
  ${({ $alignment }) =>
    $alignment === "right" &&
    `
    margin-left: auto;
    margin-right: 0;
  `}
`;

const PopupContainer = styled.div`
  display: flex;
  width: 376px;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  border-radius: 12px;
  background: var(--Colors-Neutral-100, #fff);
`;

// Sticky header inside modal
const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
  padding: ${({ $isSlimModal }) => ($isSlimModal ? "24px" : "19px 60px")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10; // Keep it above the scrollable content

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

// Title for the modal
const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
`;

const ModalSubtitle = styled.div`
  margin-top: 8px;
  color: var(--Colors-Neutral-700);
  font-size: 14px;
  line-height: 1.4;
`;

// Button group (Cancel/Save)
const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

// Scrollable content section
const ModalContent = styled.div`
  padding: 20px;
  overflow-y: auto; // Make the content scrollable
  flex-grow: 1; // Allow the content to take available space
`;

// To have the bottom buttons for some scenarios for non popup
const FooterContainer = styled.div`
  border-top: 1px solid #e9ecef;
  padding: ${({ $isSlimModal }) => ($isSlimModal ? "24px" : "19px 60px")};
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  & > button {
    flex: 1;
  }
`;
